/**
 * PLUGIN REGISTRY — Registro dei plugin e dei tipi entità.
 * Popolato al boot da pluginLoader.ts. Read-only dopo il bootstrap.
 */

import type { PluginManifest } from './types';
import type { Reducer } from '@reduxjs/toolkit';
import { registerVocabularyEntries } from './vocabularyRegistry';
import { registerEntityType } from '../entityRegistry/entityRegistry';
import { CORE_SLICE_NAMES } from '../store/coreReducers';

const plugins: PluginManifest[] = [];

/** Gli slot la cui selezione vive nell'URL: lì lo slug è obbligatorio e unico. */
const SLUG_SLOTS: readonly string[] = ['detail-center:activity-tab', 'settings:section'];

/**
 * I segmenti di /settings che appartengono al core (SettingsPage e la rotta
 * /settings/user): una sezione di plugin con uno di questi slug non sarebbe
 * raggiungibile. Aggiungendo una sezione core, va aggiunta qui.
 */
const CORE_SETTINGS_SLUGS: readonly string[] = [
  'company', 'teams', 'pipelines', 'queues', 'user',
];

/**
 * Id dei plugin caricati come DEMO, cioè arrivati dalla glob condizionata di
 * `pluginLoader`. Servono a `useEnabledPlugins`, che li lascia passare senza
 * consultare `user.plugins`.
 *
 * Lo sa il registro e non il manifest. Se fosse un campo di `PluginManifest`, chi
 * scrive un plugin dovrebbe impararlo, e soprattutto qualunque manifest potrebbe
 * dichiararsi esente dall'autorizzazione del backend. Così invece lo decide il
 * loader, che è l'unico a sapere da quale glob è arrivato il modulo — e da quella
 * glob, in produzione, non arriva niente.
 */
const demoPluginIds = new Set<string>();

export function isDemoPlugin(id: string): boolean {
  return demoPluginIds.has(id);
}

export function registerPlugin(manifest: PluginManifest, isDemo = false): void {
  // Voci di vocabolario contribuite: vanno registrate qui e non al primo uso,
  // perché il boundary REST può incontrare un valore contribuito prima che
  // qualunque componente del plugin sia montato - basta aprire un preventivo
  // con un allegato DXF.
  if (manifest.vocabularyEntries)
    registerVocabularyEntries(manifest.vocabularyEntries);

  if (plugins.some((p) => p.id === manifest.id)) {
    throw new Error(`[Plugin Registry] Plugin "${manifest.id}" già registrato.`);
  }

  // Il set parte dalle slice del CORE e non dalle sole slice degli altri
  // plugin. La collisione col core è la più grave delle due e prima non
  // veniva vista: `createAppStore` fonde i due insiemi con uno spread, dove a
  // parità di chiave vince il plugin, quindi una slice chiamata `quotations`
  // avrebbe sostituito quella del prodotto senza un errore e senza un warning.
  // Fra plugin il danno è simmetrico e si nota subito; col core no, perché a
  // rompersi sono schermate che chi scrive il plugin non sta guardando.
  const existingSliceNames = new Set([
    ...CORE_SLICE_NAMES,
    ...plugins.flatMap((p) => p.slices.map((s) => s.name)),
  ]);
  for (const slice of manifest.slices) {
    if (existingSliceNames.has(slice.name)) {
      const owner = CORE_SLICE_NAMES.includes(slice.name)
        ? 'del core'
        : 'di un altro plugin';

      throw new Error(
        `[Plugin Registry] Conflitto slice: "${slice.name}" del plugin ` +
        `"${manifest.id}" è già una slice ${owner}. Prefissa le slice del ` +
        'plugin per non collidere.'
      );
    }
    // Aggiunta subito: intercetta anche due slice con lo stesso nome dentro lo
    // stesso manifest, che altrimenti passerebbero e ne perderebbero una.
    existingSliceNames.add(slice.name);
  }

  // Slot contributor: la `key` deve essere univoca nel set filtrato, cioè per
  // (entityType, slot). Il set parte dai plugin già registrati e cresce mentre
  // si scorre il manifest, così intercetta anche i duplicati interni allo
  // stesso plugin — che sono il caso più probabile (copia-incolla di un
  // contributor senza rinominare la key) e a runtime darebbero solo il warning
  // React sulle key duplicate, con uno dei due contributi che sparisce.
  const slotKeys = new Set(
    plugins.flatMap((p) =>
      (p.slotContributors ?? []).map((c) => `${c.entityType}|${c.slot}|${c.key}`)
    )
  );
  // Slug delle tab fra i tipi di comunicazione: sono SEGMENTI URL, quindi la
  // collisione non è un dettaglio estetico come per le key — due contributi che
  // chiedono /contacts/1/preventivi si contendono una rotta, e a decidere chi
  // vince sarebbe l'ordine di registrazione. Meglio fallire al boot.
  //
  // Vale anche per le sezioni delle Impostazioni (/settings/<slug>), che in
  // più non possono prendere uno slug del core: la rotta sarebbe la stessa.
  const slugKeys = new Set(
    plugins.flatMap((p) =>
      (p.slotContributors ?? [])
        .filter((c) => SLUG_SLOTS.includes(c.slot) && c.slug)
        .map((c) => `${c.entityType}|${c.slot}|${c.slug}`)
    )
  );
  for (const contributor of manifest.slotContributors ?? []) {
    const slotKey = `${contributor.entityType}|${contributor.slot}|${contributor.key}`;
    if (slotKeys.has(slotKey)) {
      throw new Error(
        `[Plugin Registry] Conflitto slot contributor: key "${contributor.key}" ` +
        `già presente su ${contributor.entityType}/${contributor.slot} ` +
        `(plugin "${manifest.id}").`
      );
    }
    slotKeys.add(slotKey);

    if (SLUG_SLOTS.includes(contributor.slot)) {
      if (!contributor.slug) {
        throw new Error(
          `[Plugin Registry] Il contributo "${contributor.key}" su ` +
          `${contributor.entityType}/${contributor.slot} non ha uno slug: ` +
          'quella posizione vive nell\'URL e senza slug non sarebbe raggiungibile ' +
          `(plugin "${manifest.id}").`
        );
      }
      const slugKey = `${contributor.entityType}|${contributor.slot}|${contributor.slug}`;
      if (
        slugKeys.has(slugKey) ||
        (contributor.slot === 'settings:section' && CORE_SETTINGS_SLUGS.includes(contributor.slug))
      ) {
        throw new Error(
          `[Plugin Registry] Conflitto slug: "${contributor.slug}" è già usato su ` +
          `${contributor.entityType}/${contributor.slot} (plugin "${manifest.id}").`
        );
      }
      slugKeys.add(slugKey);
    }
  }

  plugins.push(manifest);

  if (isDemo) demoPluginIds.add(manifest.id);

  // Registra i tipi entità del plugin nell'EntityRegistry
  if (manifest.entityTypes) {
    for (const entityType of manifest.entityTypes) {
      registerEntityType(entityType);
    }
  }
}

export function getPlugins(): readonly PluginManifest[] {
  return plugins;
}

// Qui c'erano getPluginRoutes() e getPluginMenuItems(). Non erano chiamate da
// nessuno: rotte e voci di menu le compongono AppRouter e Sidebar partendo da
// useEnabledPlugins, che filtra sui plugin abilitati per l'utente
// (state.auth.user.plugins).
//
// Non erano solo codice morto, erano una trappola: leggevano `plugins` diretto,
// cioè TUTTI i plugin presenti nel bundle, senza guardare l'abilitazione. Chi
// le avesse trovate cercando "come ottengo le rotte dei plugin" avrebbe
// registrato anche le rotte di plugin che l'utente non ha.
//
// Il reducer sotto legge invece `plugins` a ragione: lo store si costruisce una
// volta al boot e deve contenere le slice di tutti i plugin caricati.
// L'abilitazione riguarda cosa si vede, non cosa esiste nello store.
export function getPluginReducers(): Record<string, Reducer> {
  const reducers: Record<string, Reducer> = {};
  for (const plugin of plugins) {
    for (const slice of plugin.slices) {
      reducers[slice.name] = slice.reducer;
    }
  }
  return reducers;
}
